import AlertModalDelete from "components/AlertModal/AlertModalDelete";
import { AuthContext } from "context/appContext";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { BsFillCreditCardFill } from "react-icons/bs";
import { FaRegCommentDots, FaUser } from "react-icons/fa";
import { IoTimeSharp } from "react-icons/io5";
import { MdAccessTimeFilled } from "react-icons/md";
import { PiUsersFourFill } from "react-icons/pi";
import { RiQrScanFill } from "react-icons/ri";
import {
  cancelReservation,
  fetchReservationByClient,
  fetchReservationByOwner,
  fetchReservationByOwnerAsClient,
  repayReservationFee,
} from "redux/features/reservations/reservationSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { socket } from "../../utils/socket";
import CommentModal from "./CommentModal";

export interface ClientReservationDetailedCardProps {
  className?: string;
  defaultOpen?: boolean;
  data: any;
  totalCount?: number;
  setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ClientReservationDetailedCard: FC<ClientReservationDetailedCardProps> = ({
  className = "",
  data,
  totalCount,
  defaultOpen = false,
}) => {
  const dispatch = useAppDispatch();

  let reservationState = useAppSelector((state) => state.featureReservation);
  const authContext = useContext(AuthContext);
  const profilId = authContext?.user?.profil?.id;
  const profil_status = authContext?.user?.profil?.validation;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    if (reservationState.sessionUrl) {
      window.location.href = reservationState.sessionUrl;
    }
  }, [reservationState.sessionUrl]);

  useEffect(() => {
    if (reservationState.status === "failed") {
      toast.error("🔔 Quelque chose ne fonctionne pas, veuillez réessayer!");
    }
  }, [reservationState.status]);

  function formatDateShort(dateString: any) {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const handleClick = async () => {
    if (setShowModal) {
      setShowModal(true);
    }
  };

  const handleConfirm = async () => {
    setShowModal(false);
    setLoading(true);
    await dispatch(cancelReservation({ reservation: data.id }));
    setCanceled(true);
    setLoading(false);

    if (profilId) {
      if (profil_status !== "en cours") {
        await dispatch(fetchReservationByOwner({ id: profilId, page: 1 }));

        await dispatch(fetchReservationByOwnerAsClient({ id: profilId, page: 1 }));
      }
      await dispatch(fetchReservationByClient({ id: profilId, page: 1 }));
    }

    if (data) {
      socket.emit("send-notification", {
        senderId: data.Client.userId,
        receiverId: data.Owner.userId,
        action: "cancel_reservation",
        reservationId: data.id,
        reservation: data,
      });
    }
  };

  const handleSubmit = async () => {
    dispatch(repayReservationFee(data.id));
  };

  const [showCommentModal, setShowCommentModal] = useState(false);

  const renderDetail = () => {
    if (!isOpen) return null;

    return (
      <div className="p-4 border rounded-2xl border-neutral-200 dark:border-neutral-700">
        <div>
          <AlertModalDelete
            open={showModal}
            handleDelete={() => handleConfirm()}
            handleClose={() => setShowModal(false)}
            message={`Êtes-vous sûr(e) de vouloir annuler votre réservation ?`}
            alertText="Annulation de Réservation"
          />
          <div className="flex flex-col justify-around md:flex-row">
            <div className="flex my-5 md:my-0">
              <div className="flex flex-col items-center flex-shrink-0 py-2"></div>
              <div className="ml-4 space-y-10 text-sm">
                <div className="flex flex-col space-y-1">
                  <span className="font-semibold text-neutral-950">
                    <div className="flex space-x-2">
                      <FaUser className="w-4 h-4 dark:text-white" />
                      <span className="dark:text-white">Propriétaire:</span>
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {data?.Owner?.name ? `${data?.Owner?.name}  ` : data?.Owner?.companyName}
                      </span>
                    </div>
                  </span>
                </div>

                <div className="flex flex-col space-y-1">
                  <span className="font-semibold text-neutral-950">
                    <div className="flex space-x-2">
                      <IoTimeSharp className="w-5 h-5 dark:text-white" />
                      <span className="dark:text-white">Heure d'entrée:</span>
                      <span className="text-neutral-500 dark:text-neutral-400">{data?.startHour}</span>
                    </div>
                  </span>
                </div>

                <div className="flex flex-col space-y-1">
                  <span className="font-semibold text-neutral-950">
                    <div className="flex space-x-2">
                      <MdAccessTimeFilled className="w-5 h-5 dark:text-white" />
                      <span className="dark:text-white">Heure de sortie:</span>
                      <span className="text-neutral-500 dark:text-neutral-400">{data?.endHour}</span>
                    </div>
                  </span>
                </div>

                <div className="flex flex-col space-y-1">
                  <span className="font-semibold text-neutral-950">
                    <div className="flex space-x-2">
                      <PiUsersFourFill className="w-5 h-5 dark:text-white" />
                      <span className="dark:text-white">Personnes autorisées:</span>
                      <span className="text-neutral-500 dark:text-neutral-400">{data?.visitor}</span>
                    </div>
                  </span>
                </div>
              </div>
            </div>
            <div className="border-l border-neutral-200 dark:border-neutral-700 md:mx-6 lg:mx-10"></div>
            <ul className="space-y-1 text-sm text-neutral-500 dark:text-neutral-400 md:space-y-2">
              <li className="font-semibold text-neutral-950 dark:text-white">
                <div className="flex space-x-2">
                  <BsFillCreditCardFill className="w-5 h-5 dark:text-white" />
                  <span className="dark:text-white"> Détail paiement:</span>
                </div>{" "}
              </li>
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                <span>Sous-total </span>
                <span> {data?.halfTotal} €</span>
              </div>
              {data?.extra && data?.extra !== 0 ? (
                <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                  <span>Extra </span>
                  <span>{data?.extra} €</span>
                </div>
              ) : null}
              {data?.cleaning_costs && data?.cleaning_costs !== 0 ? (
                <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                  <span>Frais de ménage </span>
                  <span>{data?.cleaning_costs} €</span>
                </div>
              ) : null}
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                <span>Service </span>
                <span> {data?.service} €</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>{data?.price} €</span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="font-semibold text-neutral-950 dark:text-white">
                  <div className="flex space-x-2">
                    <RiQrScanFill className="w-5 h-5 dark:text-white" />
                    <span className="dark:text-white">Code de réservation:</span>
                    <span className="text-neutral-500 dark:text-neutral-400">{data?.reference}</span>
                  </div>
                </span>
              </div>
            </ul>
            <br />
          </div>
        </div>
        <div className="flex justify-center space-y-5 ">
          <div className="border-t border-neutral-200 dark:border-neutral-700" />

          {data?.action === "cancel" ? (
            <ButtonPrimary loading={loading} className="bg-red-500" onClick={handleClick}>
              Annuler réservation
            </ButtonPrimary>
          ) : data?.action === "complete" ? (
            <ButtonPrimary className="bg-yellow-500" type="button" onClick={handleSubmit}>
              Terminer réservation
            </ButtonPrimary>
          ) : null}

          <div className="border-t border-neutral-200 dark:border-neutral-700" />
        </div>
      </div>
    );
  };
  return (
    <>
      <div
        className={`overflow-hidden relative p-4 space-y-6 bg-white rounded-2xl border transition-shadow nc-FlightCardgroup sm:p-6 dark:bg-neutral-900 border-neutral-100 dark:border-neutral-800 hover:shadow-lg ${className}`}
        data-nc-id="FlightCard"
      >
        <div className={`relative sm:pr-20 ${className}`} data-nc-id="FlightCard">
          {/* <a href={`/location/${data?.Location?.id}`} className="absolute inset-0">
            <span></span>
          </a> */}

          <span
            className={`absolute right-0 bottom-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 w-10 h-10 bg-neutral-50 dark:bg-neutral-800 rounded-full flex items-center justify-center cursor-pointer ${
              isOpen ? "transform -rotate-180" : ""
            }`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <i className="text-xl las la-angle-down"></i>
          </span>

          <div className="flex flex-col justify-between">
            {/* FOR MOBILE RESPONSIVE */}
            <div className="block ml-12 space-y-1 lg:hidden">
              <div className="flex items-center font-semibold">
                <div className="flex-1">
                  <a href={`/location/${data?.Location?.id}`} className="absolute inset-0 cursor-pointer">
                    {data?.Location?.title}
                  </a>
                </div>
                <div className="flex justify-center w-12">
                  <i className="text-2xl las la-long-arrow-alt-right"></i>
                </div>
                <div className="flex-1 text-right">
                  <span className="text-sm font-normal text-neutral-500">
                    {formatDateShort(data?.selectedDate)}
                  </span>
                </div>
              </div>

              <div className="flex items-center font-semibold">
                <div className="flex-1">Prix:</div>
                <div className="flex justify-center w-12">
                  <i className="text-2xl las la-long-arrow-alt-right"></i>
                </div>
                <div className="flex-1 text-right">
                  <span className="text-sm font-normal text-neutral-500">{(data?.price / 100).toFixed(2)} €</span>
                </div>
              </div>
            </div>

            {/* TIME - NAME */}

            <div className="flex justify-between ml-12 space-y-1">
              <div className="flex-shrink-0 w-24 lg:w-32 hidden lg:block  mr- flex-[4] ">
                <img
                  src={`${process.env.REACT_APP_CLOUD_FRONT}${data?.Location?.images[0]}`}
                  className="w-24 h-24 mb-4 rounded-md"
                  alt=""
                />
              </div>

              <div className="hidden lg:block min-w-[150px] flex-[4] ml-12">
                <a href={`/location/${data?.Location?.id}`} className="cursor-pointer ">
                  <div className="text-lg font-medium">Espace</div>
                  <div className="text-sm text-neutral-500 font-normal mt-0.5">{data?.Location?.title}</div>
                </a>
              </div>

              <div className="hidden lg:block flex-[4] whitespace-nowrap ml-4">
                <div className="text-lg font-medium">Date</div>
                <div className="text-sm text-neutral-500 font-normal mt-0.5">
                  {formatDateShort(data?.selectedDate)}
                </div>
              </div>

              <div className="hidden lg:block flex-[4] whitespace-nowrap ml-4">
                <div className="text-lg font-medium">Prix</div>
                <div className="text-sm text-neutral-500 font-normal mt-0.5">{data?.price} €</div>
              </div>

              <div className="hidden lg:block flex-[4] whitespace-nowrap ml-4">
                <div className="flex flex-col items-center">
                  <div
                    className={`text-xl font-semibold ${
                      data.status === "En cours"
                        ? "text-green-600"
                        : data.status === "En attente" || data.status === "Non complété"
                          ? "text-yellow-600"
                          : data.status === "Annulée"
                            ? "text-red-600"
                            : ""
                    }`}
                  >
                    {data?.status}
                  </div>

                  {data.status === "Terminée" && (
                    <div className="flex justify-center mt-8">
                      <button
                        className="relative flex items-center justify-center px-4 py-2 space-x-3 text-white transition-all duration-300 ease-in-out transform rounded-full shadow-md group bg-gradient-to-r from-blue-500 to-indigo-600 hover:shadow-lg hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
                        onClick={() => setShowCommentModal(true)}
                      >
                        <FaRegCommentDots className="w-5 h-5 text-white transition-transform duration-200 group-hover:scale-110" />
                        <span className="text-sm font-medium tracking-wide">Laisser un avis</span>
                        <div className="absolute inset-0 transition-opacity duration-300 rounded-full opacity-0 bg-eventToBee group-hover:opacity-20"></div>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {showCommentModal && (
                <CommentModal
                  open={showCommentModal}
                  handleClose={() => setShowCommentModal(false)}
                  locationId={data?.Location?.id}
                />
              )}
            </div>
            {(data.status === "En cours" || data.status === "Terminée") && (
              <div className="flex justify-between pl-20">
                <div className="text-sm">
                  <span className="font-bold">Adresse :</span>
                  {data?.Location?.city + ", " + data?.Location?.address}
                </div>
                <div className="text-sm">
                  <span className="font-bold">Numéro Téléphone :</span> +33 {data?.Owner?.phone}
                </div>
              </div>
            )}
          </div>
        </div>

        {renderDetail()}
      </div>
    </>
  );
};

export default ClientReservationDetailedCard;
